/**
 * Spec 220 — the card an engine gets while its provider is disabled.
 *
 * A disabled engine produces no visibility rows, so `aggregateByEngine` skips
 * it and it disappears from "Visibility by engine" entirely — correctly, since
 * there is nothing to average. The problem was never the omission; it was that
 * the omission said nothing. Mistral was gone for 34 days and the page it was
 * missing from never mentioned it.
 *
 * This card takes NO aggregation and therefore CANNOT render a score. That is
 * deliberate and structural: threading an all-null aggregation through
 * `EngineVisibilityCard` would put a null check on every metric and invite a
 * future `?? 0`. A paused engine has not measured zero — it has not measured,
 * the same null-versus-zero line specs 214, 215, 218 and 219 each had to hold.
 */
import { Icon } from './ui/Icon.js';
import { ENGINE_LABELS } from '../types/index.js';

export interface PausedEngineCardProps {
  engine: string;
  /** When the provider was disabled — ISO, from the flag's own updated_at. */
  since: string | null;
  /**
   * The server's kind-aware sentence. Built where `kind` is known, so an
   * operator disable never claims a bad key.
   */
  reason: string | null;
}

function formatSince(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function PausedEngineCard({ engine, since, reason }: PausedEngineCardProps) {
  const label = ENGINE_LABELS[engine] ?? engine;
  const sinceLabel = formatSince(since);

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white flex flex-col gap-2 border-amber-200 bg-amber-50/40 p-4"
      data-testid={`paused-engine-card-${engine}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          <Icon name="bx-pause-circle" />
          Paused
        </span>
      </div>

      {/* No scores, by construction. The date is the flag's own updated_at —
          when collection actually stopped — rather than an inferred
          "last data seen", which would be a claim GEO Observatory cannot defend. */}
      <p className="text-xs text-slate-600">
        {sinceLabel ? <>Collection paused since {sinceLabel}.</> : <>Collection is paused.</>}
      </p>
      {reason && <p className="text-xs leading-relaxed text-slate-500">{reason}</p>}
      <p className="mt-auto pt-1 text-xs text-slate-400">
        No scores for this engine while it is paused — this is missing data, not a zero.
      </p>
    </div>
  );
}
